import { type ErrorComponentProps, Link, useRouter } from '@tanstack/react-router'
import { useState } from 'react'
import { Button, buttonVariants } from '@/components/ui/button'

/** Where it happened and the first frames of the stack — without the message, which the page
 *  already says once (a second copy on screen is a second match for anything finding it). */
function whereItBroke(error: Error): string {
  const frames = (error.stack ?? '')
    .split('\n')
    .filter(
      (line) => !line.includes(error.message) && (/^\s*at\s/.test(line) || line.includes('@')),
    )
    .slice(0, 12)
    .join('\n')
  return [window.location.pathname, frames].join('\n\n')
}

/** What a bug report needs: what threw, where, and the stack. */
const errorDetails = (error: Error) =>
  [`${error.name}: ${error.message}`, whereItBroke(error)].join('\n\n')

/**
 * What a route shows when its loader or render throws. The router's stock component is a bare
 * "Something went wrong!" with the raw error in red monospace and no way back; this keeps the
 * app's chrome, says it in a sentence, and offers the two things that help — try again, or leave.
 * The stack sits behind "Technical details", with a Copy button: the browser keeps no record of a
 * crash anywhere we can read, so what someone pastes is how it gets fixed.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const [copied, setCopied] = useState(false)
  return (
    <div
      role="alert"
      className="mx-auto my-10 flex max-w-lg flex-col gap-3 rounded-md border border-border bg-card p-6"
    >
      <p className="text-xs font-semibold tracking-wide text-destructive uppercase">
        Something went wrong
      </p>
      <h1 className="text-lg font-semibold text-foreground">This page could not load.</h1>
      <p className="text-sm text-muted-foreground">{error.message}</p>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          Try again
        </Button>
        <Link to="/app/accounts" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          Back to accounts
        </Link>
      </div>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer select-none">Technical details</summary>
        <pre className="mt-2 max-h-56 overflow-auto rounded border border-border bg-muted p-2 text-[11px] whitespace-pre-wrap">
          {whereItBroke(error)}
        </pre>
        <Button
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(errorDetails(error))
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }}
        >
          {copied ? 'Copied' : 'Copy details'}
        </Button>
      </details>
    </div>
  )
}
